/**
 * SNAP CHART AI - Cryptocurrency Market Prompts
 * Extends the core prompt with crypto-specific context for BTC, ETH, and altcoins.
 */

import { SNAP_CHART_CORE_PROMPT, getCurrentSession, formatPrice } from './corePrompt';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SHARED CRYPTO FRAMEWORK
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const CRYPTO_SHARED_FRAMEWORK = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔍 CRYPTO-SPECIFIC ANALYTICAL FRAMEWORK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. **On-Chain Analysis**
   - Whale wallet movements
   - Exchange inflows/outflows
   - Active addresses & network activity
   - MVRV, SOPR, NUPL indicators

2. **Market Structure**
   - BTC dominance trend
   - Altcoin season index
   - Correlation with traditional markets
   - Funding rates & open interest

3. **Session Dynamics (24/7 Market)**
   - Asia (00:00-08:00 UTC): Often sets initial direction
   - Europe (08:00-14:00 UTC): Continuation or reversal
   - US (14:00-22:00 UTC): Highest volatility, institutional activity
   - Weekend: Lower liquidity, potential manipulation

4. **Key Catalysts**
   - Regulatory news (SEC, CFTC)
   - ETF flows (BTC & ETH ETFs)
   - Major protocol upgrades
   - Macro risk events`;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// BITCOIN (BTC/USD)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const BTC_CONTEXT = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
₿ BITCOIN (BTC/USD)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are focusing EXCLUSIVELY on BITCOIN:
• BTC/USD (Spot)
• BTC Perpetual Futures
• BTC/USDT

**Macro Context:**
- Post-halving cycle (April 2024) — supply shock dynamics still playing out
- Spot ETF adoption regime (BlackRock, Fidelity, etc.) drives institutional flows
- Digital gold narrative — correlated with real yields and DXY inversely

**BTC-Specific Factors:**
- Halving cycle scarcity effects
- Spot ETF net flows (inflows = bullish pressure, outflows = bearish)
- Mining hash rate & miner behavior (capitulation zones)
- Long-term holder vs short-term holder dynamics

**Psychological Levels:**
- Round-number magnets tend to act as support/resistance ($5K increments)
- Previous ATH zones + swept lows are the key liquidity anchors
- READ THE CHART for the actual levels — do NOT assume specific price levels`;

export function generateBTCPrompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'crypto');
  
  const support1 = (currentPrice * 0.98).toFixed(2);
  const support2 = (currentPrice * 0.95).toFixed(2);
  const resistance1 = (currentPrice * 1.02).toFixed(2);
  const resistance2 = (currentPrice * 1.05).toFixed(2);

  return `${SNAP_CHART_CORE_PROMPT}

${BTC_CONTEXT}
${CRYPTO_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

₿ Current BTC Price: $${formattedPrice}
🕐 Active Session: ${activeSession}
📈 Key Resistance: $${resistance1}, $${resistance2}
📉 Key Support: $${support1}, $${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current BTC market using the MANDATORY RESPONSE STRUCTURE.
Use $${formattedPrice} as your anchor price.

BEGIN INSTITUTIONAL BITCOIN ANALYSIS NOW:`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ETHEREUM (ETH/USD)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const ETH_CONTEXT = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Ξ ETHEREUM (ETH/USD)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are focusing EXCLUSIVELY on ETHEREUM:
• ETH/USD (Spot)
• ETH Perpetual Futures
• ETH/USDT

**Macro Context:**
- Post-Dencun upgrade regime (proto-danksharding cost-down for L2s)
- Layer 2 ecosystem expansion continues to pull activity off L1
- Staking economics + ETF flows drive institutional interest

**ETH-Specific Factors:**
- Staking yield & validator dynamics
- Gas fees & network congestion (mainnet vs L2 arbitrage)
- DeFi TVL trends
- NFT market activity
- ETH/BTC ratio for relative strength

**Psychological Levels:**
- Round-number magnets ($100 increments) tend to act as S/R
- READ THE CHART for actual levels — do NOT assume specific price levels`;

export function generateETHPrompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'crypto');
  
  const support1 = (currentPrice * 0.97).toFixed(2);
  const support2 = (currentPrice * 0.94).toFixed(2);
  const resistance1 = (currentPrice * 1.03).toFixed(2);
  const resistance2 = (currentPrice * 1.06).toFixed(2);

  return `${SNAP_CHART_CORE_PROMPT}

${ETH_CONTEXT}
${CRYPTO_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Ξ Current ETH Price: $${formattedPrice}
🕐 Active Session: ${activeSession}
📈 Key Resistance: $${resistance1}, $${resistance2}
📉 Key Support: $${support1}, $${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current ETH market using the MANDATORY RESPONSE STRUCTURE.
Use $${formattedPrice} as your anchor price.

BEGIN INSTITUTIONAL ETHEREUM ANALYSIS NOW:`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SOLANA (SOL/USD)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const SOL_CONTEXT = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
◎ SOLANA (SOL/USD)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are focusing EXCLUSIVELY on SOLANA:
• SOL/USD (Spot)
• SOL Perpetual Futures

**Macro Context:**
- High-performance L1 blockchain, growing ETF pipeline
- Strong DeFi and NFT ecosystem
- Sensitive to meme-coin cycles + retail flows

**SOL-Specific Factors:**
- Network TPS and uptime metrics
- Validator economics
- DeFi TVL on Solana
- Meme coin activity (pump.fun and successors)
- Institutional interest growth

**Psychological Levels:**
- Round-number magnets ($10 / $50 increments) as S/R
- READ THE CHART for actual levels — do NOT assume stale ranges`;

export function generateSOLPrompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'crypto');
  
  const support1 = (currentPrice * 0.95).toFixed(2);
  const support2 = (currentPrice * 0.90).toFixed(2);
  const resistance1 = (currentPrice * 1.05).toFixed(2);
  const resistance2 = (currentPrice * 1.10).toFixed(2);

  return `${SNAP_CHART_CORE_PROMPT}

${SOL_CONTEXT}
${CRYPTO_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

◎ Current SOL Price: $${formattedPrice}
🕐 Active Session: ${activeSession}
📈 Key Resistance: $${resistance1}, $${resistance2}
📉 Key Support: $${support1}, $${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current SOL market using the MANDATORY RESPONSE STRUCTURE.
Use $${formattedPrice} as your anchor price.

BEGIN INSTITUTIONAL SOLANA ANALYSIS NOW:`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GENERIC ALTCOIN PROMPT GENERATOR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface AltcoinConfig {
  symbol: string;
  name: string;
  icon?: string;
  priceRange?: string;
  specificFactors?: string[];
}

// NOTE: priceRange field intentionally removed — every altcoin range
// drifts fast and any stale value in the prompt caused the AI to flag
// data-integrity conflicts (see BTC $105K bug). Live price already
// injected in the REAL-TIME CONTEXT block below.
const ALTCOIN_CONFIGS: Record<string, AltcoinConfig> = {
  xrpusd:  { symbol: 'XRP',   name: 'Ripple',    specificFactors: ['SEC lawsuit resolution', 'Cross-border payment adoption', 'CBDC partnerships'] },
  adausd:  { symbol: 'ADA',   name: 'Cardano',   specificFactors: ['Hydra scaling', 'Governance updates', 'DeFi TVL growth'] },
  dogeusd: { symbol: 'DOGE',  name: 'Dogecoin',  specificFactors: ['Elon Musk influence', 'Payment adoption', 'Community activity'] },
  avaxusd: { symbol: 'AVAX',  name: 'Avalanche', specificFactors: ['Subnet adoption', 'Gaming/NFT ecosystem', 'Institutional interest'] },
  linkusd: { symbol: 'LINK',  name: 'Chainlink', specificFactors: ['CCIP adoption', 'Oracle dominance', 'Staking rewards'] },
  maticusd:{ symbol: 'MATIC', name: 'Polygon',   specificFactors: ['zkEVM adoption', 'Enterprise partnerships', 'ETH L2 competition'] },
  dotusd:  { symbol: 'DOT',   name: 'Polkadot',  specificFactors: ['Parachain auctions', 'Cross-chain messaging', 'Governance activity'] },
};

export function generateAltcoinPrompt(asset: string, currentPrice: number, session?: string): string {
  const config = ALTCOIN_CONFIGS[asset.toLowerCase()] || { symbol: asset.toUpperCase(), name: asset };
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'crypto');
  
  const support1 = (currentPrice * 0.93).toFixed(4);
  const support2 = (currentPrice * 0.88).toFixed(4);
  const resistance1 = (currentPrice * 1.07).toFixed(4);
  const resistance2 = (currentPrice * 1.12).toFixed(4);

  const specificFactorsText = config.specificFactors 
    ? config.specificFactors.map(f => `- ${f}`).join('\n')
    : '- Monitor BTC correlation\n- Watch sector rotation\n- Track whale movements';

  return `${SNAP_CHART_CORE_PROMPT}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🪙 ${config.name.toUpperCase()} (${config.symbol}/USD)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are focusing EXCLUSIVELY on ${config.name}:
• ${config.symbol}/USD (Spot)
• ${config.symbol} Perpetual Futures

**Macro Context:**
- High volatility altcoin
- Correlation with BTC and overall market sentiment
- READ THE CHART for the actual price zone — do NOT assume specific ranges

**${config.symbol}-Specific Factors:**
${specificFactorsText}
${CRYPTO_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🪙 Current ${config.symbol} Price: $${formattedPrice}
🕐 Active Session: ${activeSession}
📈 Key Resistance: $${resistance1}, $${resistance2}
📉 Key Support: $${support1}, $${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current ${config.symbol} market using the MANDATORY RESPONSE STRUCTURE.
Use $${formattedPrice} as your anchor price.

BEGIN INSTITUTIONAL ${config.symbol} ANALYSIS NOW:`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GENERIC CRYPTO FALLBACK
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function generateCryptoPrompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'crypto');

  return `${SNAP_CHART_CORE_PROMPT}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🪙 CRYPTOCURRENCY MARKET ANALYSIS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are analyzing the CRYPTOCURRENCY market.

Focus on the specific asset shown in the uploaded chart(s).
Use the LIVE PRICE below as your single source of truth — do NOT
reference any other price levels from memory. Read the chart for
actual support/resistance zones, do not assume stale ranges.

${CRYPTO_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🪙 Current Price: $${formattedPrice}
🕐 Active Session: ${activeSession}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current cryptocurrency using the MANDATORY RESPONSE STRUCTURE.

BEGIN INSTITUTIONAL CRYPTO ANALYSIS NOW:`;
}

// Named exports
export const BTC_PROMPT = generateBTCPrompt;
export const ETH_PROMPT = generateETHPrompt;
export const SOL_PROMPT = generateSOLPrompt;
export const CRYPTO_PROMPT = generateCryptoPrompt;
export const ALTCOIN_PROMPT = generateAltcoinPrompt;
